import router from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { KaimonoWebRoutes } from 'src/config/routes';
import { useDebounce } from 'src/hooks/useDebounce';
import { useModal } from 'src/hooks/useModal';
import { useOverlay } from 'src/hooks/useOverlay';
import { fetchAutoCompletionKeywords, fetchPopularSearchKeywords } from 'src/libs/api/searchApi';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';
import { Presenter } from './Presenter';
import { TEST_GROUP_PATTERN, useOptimizeNext } from 'src/hooks/useOptimizeNext';
import { fetchVaisrAutoCompletionKeywords } from 'src/libs/api/vairsApi';
import { useAttributionTokens } from 'src/hooks/useAttributionToken';
import { getCookieValue } from 'src/utils/createDataLayerObject';

const SEARCH_HISTORY_LIMIT = 10;
const SEARCH_HISTORY_KEY = 'searchHistories';

export const SearchOverlay: React.FC = () => {
  const optimizeNextPattern = useOptimizeNext();
  const { updateSuggestToken } = useAttributionTokens();
  const { isOverlayVisible, setIsOverlayVisible } = useOverlay();
  const { setIsVisible, setModalParams } = useModal('noticeModal');
  const localeResources = useContext(I18nContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [completionAttributionToken, setCompletionAttributionToken] = useState<string | undefined>(undefined);
  const [searchHistories, setSearchHistories] = useState<string[]>([]);
  const [popularKeywords, setPopularKeywords] = useState<string[]>([]);
  const [autoCompletionKeywords, setAutoCompletionKeywords] = useState<string[]>([]);
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  useEffect(() => {
    const histories = JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]') as string[];
    setSearchHistories(histories);
  }, [isOverlayVisible]);

  useEffect(() => {
    const fetchKeywords = async () => {
      const keywords = await fetchPopularSearchKeywords();
      setPopularKeywords(keywords);
    };
    fetchKeywords();
  }, []);

  useEffect(() => {
    if (debouncedSearchKeyword) {
      const fetchKeywords = async () => {
        try {
          if(optimizeNextPattern === TEST_GROUP_PATTERN) {
            const visitorId = getCookieValue('_ga');
            const result = await fetchVaisrAutoCompletionKeywords(debouncedSearchKeyword, visitorId);
            const { suggestionResults, attributionToken } = result;
            setCompletionAttributionToken(attributionToken);
            setAutoCompletionKeywords(suggestionResults);
          } else {
            const keywords = await fetchAutoCompletionKeywords(debouncedSearchKeyword);
            setAutoCompletionKeywords(keywords);
          }
        } catch (error) {
          console.error('Error fetching auto completion keywords:', error);
        }
      };

      fetchKeywords();
    } else {
      setAutoCompletionKeywords([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchKeyword]);

  const handleSearchChange = (newSearchKeyword: string) => {
    setSearchKeyword(newSearchKeyword);
  };

  const handleSubmit = (keyword: string) => {
    document.body.classList.remove('no-scroll');
    setSearchKeyword('');
    if (!keyword.trim()) {
      setModalParams({
        type: 'warning',
        text: localeResources.search.enterKeywordsAlert
      });
      setIsVisible(true);
      return;
    }
    updateSearchHistories(keyword);
    setIsOverlayVisible(false);
    router.push(KaimonoWebRoutes.search(keyword));
  };

  const updateSearchHistories = (keyword: string) => {
    const histories = [...searchHistories];
    const index = histories.indexOf(keyword);
    if (index !== -1) {
      histories.splice(index, 1);
    }
    histories.unshift(keyword);
    if (histories.length > SEARCH_HISTORY_LIMIT) {
      histories.pop();
    }
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(histories));
    setSearchHistories(histories);
  };

  const handleHistoryClick = async (history: string) => {
    document.body.classList.remove('no-scroll');
    setSearchKeyword('');
    mixpanel.onSearchHistoryClicked(history);
    setIsOverlayVisible(false);
    await router.push(KaimonoWebRoutes.search(history));
    window.scrollTo(0, 0);
  };

  const handleDeleteHistory = (history: string) => {
    mixpanel.onSearchHistoryDeleteClicked(history);
    const updatedHistories = searchHistories.filter((item) => item !== history);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistories));
    setSearchHistories(updatedHistories);
  };

  const handleAutoCompletionKeywordClick = (selectedQuery: string, index: number) => {
    if (completionAttributionToken) {
      updateSuggestToken({
          completionAttributionToken,
          selectedSuggestion: selectedQuery,
          selectedPosition: index
      });
    }
    document.body.classList.remove('no-scroll');
    mixpanel.onSearchAutoCompletionKeywordClicked(searchKeyword, selectedQuery);
    setIsOverlayVisible(false);
    router.push(KaimonoWebRoutes.search(selectedQuery));
    window.scrollTo(0, 0);
  };

  const handlePopularKeywordClick = (keyword: string) => {
    document.body.classList.remove('no-scroll');
    mixpanel.onPopularKeywordClicked(keyword);
    setIsOverlayVisible(false);
    router.push(KaimonoWebRoutes.search(keyword));
    window.scrollTo(0, 0);
  };

  const handleClose = () => {
    document.body.classList.remove('no-scroll');
    setSearchKeyword('');
    setIsOverlayVisible(false);
  };

  if (!isOverlayVisible) return null;

  return (
    <Presenter
      autoCompletionKeywords={autoCompletionKeywords}
      popularKeywords={popularKeywords}
      searchKeyword={searchKeyword}
      searchHistories={searchHistories}
      onSearchChange={handleSearchChange}
      onSubmit={handleSubmit}
      onHistoryClick={handleHistoryClick}
      onDeleteHistory={handleDeleteHistory}
      onAutoCompletionKeywordClick={handleAutoCompletionKeywordClick}
      onPopularKeywordClick={handlePopularKeywordClick}
      onClose={handleClose}
      inputRef={inputRef}
      localeResources={localeResources}
    />
  );
};
